import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useSeoMetadata from '../blog/useSeoMetadata';
import { trackEvent } from '../../utils/analytics';
import {
    AI_REPLY_PRICE_RUB, ANALYTICS, AI_REPLIES_SHARE, STAFF_HOURLY_RUB, STAFF_MINUTES_PER_REPLY, BASE_PLAN_FALLBACK_RUB, COMPARE, CONTROL, DIRECTIONS, FAQ, HERO, NO_TEXT_REVIEWS_SHARE, OZON_SUBSCRIPTION_SHARE,
    OZON_SUBSCRIPTION_SHARE_NUM, PAGE_URL, PRICING, PROMO_BONUS, PROMO_CODE, PROMO_UTM, SEO, START_STEPS, STEPS,
} from './reviewsPromoContent';
import './ReviewsPromoPage.css';

const FEEDBACK_MODULE = 'ozon_feedback';

// Ссылка на регистрацию: промокод и метка источника уходят в форму и в атрибуцию пользователя
export function registerUrl(extra = {}) {
    const params = new URLSearchParams({ promo: PROMO_CODE, ...PROMO_UTM, ...extra });
    return `/?${params.toString()}#auth`;
}

function jsonLd() {
    return [
        {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: SEO.title,
            description: SEO.description,
            url: PAGE_URL,
            inLanguage: 'ru',
            isPartOf: { '@type': 'WebSite', name: 'Энилекс.Контроль', url: 'https://seller.anylex.ru/' },
        },
        {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'Энилекс: отзывы, вопросы, чаты и техподдержка Ozon',
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            description: SEO.description,
            url: PAGE_URL,
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'RUB', description: 'Работа с отзывами на бесплатном тарифе' },
        },
        {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQ.map((item) => ({
                '@type': 'Question',
                name: item.q,
                acceptedAnswer: { '@type': 'Answer', text: item.a },
            })),
        },
    ];
}

// Тарифный блок читает живой каталог тарифов: поменяли доступ в админке — страница обновилась без деплоя
function useFeedbackCatalog() {
    const [catalog, setCatalog] = useState(null);

    useEffect(() => {
        let alive = true;
        fetch('/api/v1/billing/tool-catalog')
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => { if (alive && data) setCatalog(data); })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    return useMemo(() => {
        if (!catalog) return null;
        const tools = (catalog.categories || [])
            .flatMap((category) => category.tools || category.items || [])
            .filter((tool) => tool.module_key === FEEDBACK_MODULE)
            .sort((a, b) => (a.order || 0) - (b.order || 0));
        return tools.length ? { plans: catalog.plans || [], tools } : null;
    }, [catalog]);
}

const rub = (value) => `${Math.round(value).toLocaleString('ru')} ₽`;

function NumberField({ label, value, onChange, suffix, step = 1000 }) {
    return (
        <label className="rp-field">
            <span>{label}</span>
            <div>
                <input
                    type="number" inputMode="numeric" min="0" step={step} value={value}
                    onChange={(event) => onChange(Math.max(0, Number(event.target.value) || 0))}
                />
                {suffix && <i>{suffix}</i>}
            </div>
        </label>
    );
}

// Калькулятор. Вкладка 1: подписка Ozon (она только про отзывы), вкладка 2: сотрудник против сервиса.
// Цена ответа ИИ приблизительная, зарплата и минуты на ответ — допущения, их можно менять.
function Calculator({ basePrice }) {
    const [mode, setMode] = useState('ozon');
    const [turnover, setTurnover] = useState(3000000);
    const [reviews, setReviews] = useState(2000);
    const [chatsQuestions, setChatsQuestions] = useState(500);
    const touched = useRef(false);

    const staff = mode === 'staff';
    const textReviews = reviews * (1 - NO_TEXT_REVIEWS_SHARE);
    // Подписка Ozon покрывает только отзывы: вопросы и чаты в сравнение с ней не входят
    const replies = staff ? textReviews + chatsQuestions : textReviews;
    const aiReplies = replies * AI_REPLIES_SHARE;
    const aiCost = aiReplies * AI_REPLY_PRICE_RUB;
    const plan = staff && chatsQuestions > 0 ? basePrice : 0;
    const ours = aiCost + plan;
    const theirs = staff ? (replies * STAFF_MINUTES_PER_REPLY / 60) * STAFF_HOURLY_RUB : turnover * OZON_SUBSCRIPTION_SHARE_NUM;
    const saving = theirs - ours;

    const track = () => {
        if (!touched.current) {
            touched.current = true;
            trackEvent('promo_reviews_calc_use');
        }
    };
    const pick = (next) => () => { setMode(next); trackEvent('promo_reviews_calc_mode', { mode: next }); };

    return (
        <div onChange={track} onInput={track}>
            <div className="rp-dir__tabs" role="tablist">
                <button type="button" role="tab" aria-selected={!staff} className={!staff ? 'is-on' : ''} onClick={pick('ozon')}>Подписка Ozon</button>
                <button type="button" role="tab" aria-selected={staff} className={staff ? 'is-on' : ''} onClick={pick('staff')}>Сотрудник или сервис</button>
            </div>
            <div className="rp-calc">
                <div className="rp-calc__form">
                    {!staff && <NumberField label="Оборот на Ozon в месяц с учётом соинвеста" value={turnover} onChange={setTurnover} suffix="₽" step={100000} />}
                    <NumberField label="Отзывов в месяц" value={reviews} onChange={setReviews} step={50} />
                    <small className="rp-hint">Около половины отзывов приходят без текста: ответить на них нельзя, они ничего не стоят.</small>
                    {staff && <NumberField label="Вопросов и чатов в месяц" value={chatsQuestions} onChange={setChatsQuestions} step={50} />}
                </div>
                <div className="rp-calc__result" aria-live="polite">
                    <div className="rp-calc__row rp-calc__row--old">
                        <span>{staff ? 'Сотрудник отвечает вручную' : `Подписка Ozon (${OZON_SUBSCRIPTION_SHARE})`}</span>
                        <b>{rub(theirs)}/мес</b>
                    </div>
                    <div className="rp-calc__row"><span>Ответов к обработке</span><b>{Math.round(replies).toLocaleString('ru')}</b></div>
                    <div className="rp-calc__row"><span>Энилекс: ответы ИИ</span><b>{rub(aiCost)}</b></div>
                    {plan > 0 && <div className="rp-calc__row"><span>Энилекс: тариф (вопросы и чаты)</span><b>{rub(plan)}</b></div>}
                    <div className="rp-calc__total">
                        <small>{saving >= 0 ? 'Разница в вашу пользу' : 'Разница при таких вводных'}</small>
                        <strong className={saving >= 0 ? '' : 'is-minus'}>{saving >= 0 ? rub(saving) : `−${rub(-saving)}`}<i>/мес</i></strong>
                        <span>Энилекс обойдётся в {rub(ours)}</span>
                    </div>
                    <p>
                        Расчёт ориентировочный: ИИ нужен в половине ответов, остальное закрывают правила и шаблоны. Ответ ИИ стоит около {String(AI_REPLY_PRICE_RUB).replace('.', ',')} ₽.
                        {staff ? ` Сотрудник: ${STAFF_MINUTES_PER_REPLY} минуты на ответ, ${STAFF_HOURLY_RUB} ₽ в час с налогами.` : ' Работа с отзывами на нашем бесплатном тарифе, поэтому тарифа в расчёте нет.'}
                    </p>
                </div>
            </div>
        </div>
    );
}

function Stars({ count }) {
    return <span className="rp-stars" aria-label={`${count} из 5`}>{'★'.repeat(count)}<i>{'★'.repeat(5 - count)}</i></span>;
}

// Макет интерфейса: примеры вымышленные, подпись «Пример» стоит в углу
function InboxMock() {
    return (
        <div className="rp-mock" aria-hidden="true">
            <div className="rp-mock__bar">
                <span /><span /><span />
                <b>Обращения · Ozon</b>
                <em>Пример</em>
            </div>
            <div className="rp-mock__tabs">
                <span className="is-on">Ждут <b>12</b></span><span>Черновики <b>4</b></span><span>На модерации <b>3</b></span>
            </div>

            <div className="rp-item">
                <div className="rp-item__top"><Stars count={5} /><span className="rp-tag rp-tag--free">Шаблон · 0 ₽</span></div>
                <p>Отличный крем, кожа стала мягкой. Спасибо!</p>
                <div className="rp-item__reply">Здравствуйте! Спасибо за тёплый отзыв, рады, что крем подошёл.</div>
                <div className="rp-item__foot"><span className="rp-dot rp-dot--ok" />Отправлено автоматически</div>
            </div>

            <div className="rp-item rp-item--ai">
                <div className="rp-item__top"><span className="rp-kind">Вопрос</span><span className="rp-tag rp-tag--ai">ИИ · по факту токенов</span></div>
                <p>Подойдёт ли для чувствительной кожи?</p>
                <div className="rp-item__reply">Состав без отдушек и спирта. Подойдёт для чувствительной кожи, перед первым применением сделайте тест на запястье.</div>
                <div className="rp-item__foot">
                    <span className="rp-dot rp-dot--wait" />Черновик по базе знаний
                    <button type="button" tabIndex={-1} className="rp-approve">Одобрить</button>
                </div>
            </div>

            <div className="rp-item rp-item--risk">
                <div className="rp-item__top"><Stars count={1} /><span className="rp-tag rp-tag--risk">Нужен человек</span></div>
                <p>После крема началась аллергия, буду писать жалобу.</p>
                <div className="rp-item__foot"><span className="rp-dot rp-dot--risk" />Стоп-слово: «аллерги». Автоответ запрещён</div>
            </div>
        </div>
    );
}

// Макет аналитики: числа вымышленные, подпись «Пример» стоит в углу
function AnalyticsMock() {
    const weeks = [4.3, 4.4, 4.4, 4.2, 4.0, 4.1, 4.3, 4.5, 4.6, 4.6];
    const min = 3.8;
    const max = 4.8;
    const points = weeks.map((value, index) => `${20 + index * 36},${100 - ((value - min) / (max - min)) * 84}`);
    const rows = [
        ['Крем для рук 50 мл', '4,6', '+0,3'],
        ['Сыворотка с витамином C', '4,1', '−0,2'],
        ['Маска для волос', '4,8', '+0,1'],
    ];
    return (
        <div className="rp-mock" aria-hidden="true">
            <div className="rp-mock__bar"><span /><span /><span /><b>Анализ отзывов</b><em>Пример</em></div>
            <div className="rp-an__head">
                <div><small>Рейтинг магазина</small><strong>4,6</strong><span className="rp-up">+0,3 к прошлому периоду</span></div>
                <div className="rp-an__vs"><small>Прошлый период</small><b>4,3</b></div>
            </div>
            <svg className="rp-an__chart" viewBox="0 0 360 116" role="presentation">
                <defs>
                    <linearGradient id="rpAn" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0" stopColor="#77baf0" stopOpacity="0.35" />
                        <stop offset="1" stopColor="#77baf0" stopOpacity="0" />
                    </linearGradient>
                </defs>
                <polygon points={`20,108 ${points.join(' ')} ${20 + (weeks.length - 1) * 36},108`} fill="url(#rpAn)" />
                <polyline points={points.join(' ')} fill="none" stroke="#77baf0" strokeWidth="2.5" strokeLinejoin="round" />
                <circle cx={20 + 4 * 36} cy={100 - ((weeks[4] - min) / (max - min)) * 84} r="4" fill="#f15555" />
            </svg>
            <div className="rp-an__note"><i /> Просадка в начале периода: сгруппированы причины негатива</div>
            <table className="rp-an__table">
                <tbody>
                    {rows.map(([name, rating, delta]) => (
                        <tr key={name}><td>{name}</td><td>{rating}</td><td className={delta.startsWith('+') ? 'rp-up' : 'rp-down'}>{delta}</td></tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function Pipeline() {
    const nodes = [
        { t: 'Поток обращений', s: 'Отзывы, вопросы, чаты', tag: null },
        { t: 'Правила и шаблоны', s: 'Первичный разбор без дорогой языковой модели', tag: '0 ₽', tone: 'free' },
        { t: 'ИИ по базе знаний', s: 'Только где нужен текст', tag: 'по факту токенов', tone: 'ai' },
        { t: 'Вы решаете', s: 'Черновик, одобрение или автоотправка', tag: null },
    ];
    return (
        <ol className="rp-pipe">
            {nodes.map((node, index) => (
                <li key={node.t} className={`rp-pipe__node${node.tone ? ` rp-pipe__node--${node.tone}` : ''}`}>
                    <span className="rp-pipe__n">{index + 1}</span>
                    <h3>{node.t}</h3>
                    <p>{node.s}</p>
                    {node.tag && <span className={`rp-tag rp-tag--${node.tone}`}>{node.tag}</span>}
                </li>
            ))}
        </ol>
    );
}

const MODE_LABELS = ['Выкл', 'Черновик', 'Авто'];

function ModeRow({ title, hint, active }) {
    return (
        <div className="rp-mode">
            <div><b>{title}</b><small>{hint}</small></div>
            <div className="rp-seg" role="presentation">
                {MODE_LABELS.map((label, index) => (
                    <span key={label} className={index === active ? 'is-on' : ''}>{label}</span>
                ))}
            </div>
        </div>
    );
}

function ControlMock() {
    return (
        <div className="rp-mock rp-mock--settings" aria-hidden="true">
            <div className="rp-mock__bar"><span /><span /><span /><b>Режимы ответов</b><em>Пример</em></div>
            <ModeRow title="Шаблонные ответы" hint="Бесплатно" active={2} />
            <ModeRow title="Ответы ИИ" hint="По базе знаний" active={1} />
            <ModeRow title="Нужен человек" hint="Риск, претензии" active={1} />
            <div className="rp-stop">
                <small>Стоп-слова: всегда человеку</small>
                {['аллергия', 'ожог', 'суд', 'Роспотребнадзор', 'прокуратура'].map((word) => <span key={word}>{word}</span>)}
            </div>
        </div>
    );
}

function Directions() {
    const [active, setActive] = useState(DIRECTIONS[0].id);
    const current = DIRECTIONS.find((item) => item.id === active);
    return (
        <div className="rp-dir">
            <div className="rp-dir__tabs" role="tablist">
                {DIRECTIONS.map((item) => (
                    <button
                        key={item.id} type="button" role="tab" aria-selected={item.id === active}
                        className={item.id === active ? 'is-on' : ''} onClick={() => setActive(item.id)}
                    >
                        <span aria-hidden="true">{item.icon}</span>{item.title}
                    </button>
                ))}
            </div>
            <div className="rp-dir__body" role="tabpanel">
                <div>
                    <span className={`rp-tag rp-tag--${current.plan === 'free' ? 'free' : 'base'}`}>
                        {current.plan === 'free' ? 'Бесплатный тариф' : 'С базового тарифа'}
                    </span>
                    <h3>{current.title}</h3>
                    <p>{current.text}</p>
                </div>
                <ul>
                    {current.points.map((point) => <li key={point}>{point}</li>)}
                </ul>
            </div>
        </div>
    );
}

function PlansTable({ catalog }) {
    const { plans, tools } = catalog;
    return (
        <div className="rp-tablewrap">
            <table className="rp-table rp-table--plans">
                <thead>
                    <tr>
                        <th scope="col">Что входит</th>
                        {plans.map((plan) => (
                            <th scope="col" key={plan.key}>
                                {plan.label}
                                <small>{plan.price_monthly > 0 ? `${Number(plan.price_monthly).toLocaleString('ru')} ₽/мес` : 'бесплатно'}</small>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {tools.map((tool) => (
                        <tr key={tool.key}>
                            <th scope="row">{tool.label}</th>
                            {plans.map((plan) => (
                                <td key={plan.key}>
                                    {tool.plan_values?.[plan.key]
                                        ? <span className="rp-yes" aria-label="входит">✓</span>
                                        : <span className="rp-no" aria-label="не входит">—</span>}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function ReviewsPromoPage() {
    const catalog = useFeedbackCatalog();
    const cta = registerUrl();
    const basePrice = Number(catalog?.plans?.find((plan) => plan.key === 'base')?.price_monthly) || BASE_PLAN_FALLBACK_RUB;
    const [stuck, setStuck] = useState(false);
    const pricingRef = useRef(null);

    useEffect(() => {
        trackEvent('promo_reviews_view');
        const onScroll = () => setStuck(window.scrollY > 640);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Событие, когда посетитель дошёл до тарифов
    useEffect(() => {
        const node = pricingRef.current;
        if (!node || typeof IntersectionObserver === 'undefined') return undefined;
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                trackEvent('promo_reviews_pricing_view');
                observer.disconnect();
            }
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    const onCta = (place) => () => trackEvent('promo_reviews_cta', { place });

    useSeoMetadata({
        title: SEO.title,
        description: SEO.description,
        canonicalUrl: PAGE_URL,
        openGraph: { title: SEO.title, description: SEO.description, url: PAGE_URL, type: 'website' },
        jsonLd: jsonLd(),
    });

    return (
        <div className="rp">
            <header className="rp-header">
                <Link to="/" className="rp-logo">Энилекс</Link>
                <nav aria-label="Разделы страницы">
                    <a href="#how">Как работает</a>
                    <a href="#control">Контроль</a>
                    <a href="#pricing">Тарифы</a>
                    <a href="#faq">Вопросы</a>
                </nav>
                <a className="rp-btn rp-btn--small" href={cta} onClick={onCta('header')}>Попробовать</a>
            </header>

            <main>
                <section className="rp-hero">
                    <div className="rp-hero__text">
                        <span className="rp-pill">{HERO.eyebrow}</span>
                        <h1>
                            Полная и контролируемая автоматизация отзывов, вопросов, чатов и техподдержки
                            <em> без подписки Ozon за {OZON_SUBSCRIPTION_SHARE} от оборота</em>
                        </h1>
                        <p className="rp-hero__lead">{HERO.lead}</p>
                        <div className="rp-hero__cta">
                            <a className="rp-btn" href={cta} onClick={onCta('hero')}>Зарегистрироваться</a>
                            <span>Промокод <b>{PROMO_CODE}</b>: {PROMO_BONUS} при регистрации</span>
                        </div>
                        <ul className="rp-hero__facts">
                            {HERO.facts.map((fact) => <li key={fact}>{fact}</li>)}
                        </ul>
                    </div>
                    <InboxMock />
                </section>

                <section className="rp-versus" aria-label="Сколько это стоит">
                    <div className="rp-versus__old">
                        <small>Подписка Ozon</small>
                        <strong>{OZON_SUBSCRIPTION_SHARE}</strong>
                        <span>от оборота, каждый месяц</span>
                    </div>
                    <div className="rp-versus__vs" aria-hidden="true">vs</div>
                    <div className="rp-versus__new">
                        <small>Энилекс</small>
                        <strong>0 ₽ за разбор</strong>
                        <span>шаблоны бесплатно, ИИ только по факту токенов</span>
                    </div>
                </section>

                <section className="rp-section" id="calc">
                    <span className="rp-kicker">Калькулятор</span>
                    <h2>Сколько вы сэкономите на отзывах</h2>
                    <p className="rp-section__lead">Подписка Ozon покрывает только отзывы, поэтому первое сравнение только по ним. На второй вкладке: сколько стоит сотрудник, который отвечает вручную, и сколько сервис.</p>
                    <Calculator basePrice={basePrice} />
                </section>

                <section className="rp-section" id="how">
                    <span className="rp-kicker">Как устроено</span>
                    <h2>Сначала дёшево, потом точно</h2>
                    <p className="rp-section__lead">{STEPS[1].text} Вы платите за текст, а не за сортировку потока.</p>
                    <Pipeline />
                </section>

                <section className="rp-section">
                    <span className="rp-kicker">Четыре направления</span>
                    <h2>Всё, что приходит от покупателей и Ozon, в одном окне</h2>
                    <Directions />
                </section>

                <section className="rp-section rp-control" id="analytics">
                    <AnalyticsMock />
                    <div>
                        <span className="rp-kicker">Анализ отзывов</span>
                        <h2>{ANALYTICS.title}</h2>
                        <p className="rp-section__lead">{ANALYTICS.lead}</p>
                        <ul className="rp-checks">
                            {ANALYTICS.points.map((item) => (
                                <li key={item.title}><b>{item.title}.</b> {item.text}</li>
                            ))}
                        </ul>
                    </div>
                </section>

                <section className="rp-section rp-control" id="control">
                    <div>
                        <span className="rp-kicker">Контроль</span>
                        <h2>Автоматизация, которую вы держите в руках</h2>
                        <ul className="rp-checks">
                            {CONTROL.map((item) => (
                                <li key={item.title}><b>{item.title}.</b> {item.text}</li>
                            ))}
                        </ul>
                    </div>
                    <ControlMock />
                </section>

                <section className="rp-section" id="pricing" ref={pricingRef}>
                    <span className="rp-kicker">Тарифы</span>
                    <h2>{PRICING.title}</h2>
                    <p className="rp-section__lead">{PRICING.lead}</p>
                    <div className="rp-prices">
                        {PRICING.items.map((item) => (
                            <article key={item.title}>
                                <h3>{item.title}</h3>
                                <p>{item.text}</p>
                            </article>
                        ))}
                    </div>
                    {catalog && <PlansTable catalog={catalog} />}
                </section>

                <section className="rp-section">
                    <span className="rp-kicker">Сравнение</span>
                    <h2>Энилекс и подписка Ozon</h2>
                    <div className="rp-tablewrap">
                        <table className="rp-table">
                            <thead>
                                <tr>{COMPARE.columns.map((column, index) => <th scope="col" key={column || index}>{column}</th>)}</tr>
                            </thead>
                            <tbody>
                                {COMPARE.rows.map((row) => (
                                    <tr key={row[0]}>
                                        <th scope="row">{row[0]}</th>
                                        <td>{row[1]}</td>
                                        <td className="rp-table__us">{row[2]}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                <section className="rp-section" id="start">
                    <span className="rp-kicker">Старт</span>
                    <h2>Начать можно за три шага</h2>
                    <ol className="rp-start">
                        {START_STEPS.map((step) => (
                            <li key={step.n}>
                                <span>{step.n}</span>
                                <h3>{step.title}</h3>
                                <p>{step.text}</p>
                            </li>
                        ))}
                    </ol>
                </section>

                <section className="rp-section" id="faq">
                    <span className="rp-kicker">Вопросы</span>
                    <h2>Частые вопросы</h2>
                    <div className="rp-faq">
                        {FAQ.map((item) => (
                            <details key={item.q}>
                                <summary onClick={() => trackEvent('promo_reviews_faq_open', { question: item.q.slice(0, 60) })}>{item.q}</summary>
                                <p>{item.a}</p>
                            </details>
                        ))}
                    </div>
                </section>

                <section className="rp-more">
                    <h2>Это один из множества инструментов Энилекс.Контроль</h2>
                    <p>
                        Раздел «Отзывы, вопросы, чаты» — часть платформы для продавцов Ozon и Wildberries: аналитика и юнит-экономика,
                        реклама, цены, логистика, SEO-описания, ассортимент, CRM, задачи и AI-агент. Всё работает на одних данных кабинета.
                    </p>
                </section>

                <section className="rp-final">
                    <h2>Начните с отзывов. Это бесплатно</h2>
                    <p>Промокод {PROMO_CODE} даёт {PROMO_BONUS} новым пользователям при регистрации. Подключите кабинет Ozon и включите нужные типы обращений.</p>
                    <a className="rp-btn rp-btn--light" href={cta} onClick={onCta('final')}>Зарегистрироваться</a>
                </section>
            </main>

            <div className={`rp-sticky${stuck ? ' is-on' : ''}`}>
                <a className="rp-btn" href={cta} onClick={onCta('sticky')}>Зарегистрироваться · {PROMO_BONUS}</a>
            </div>

            <footer className="rp-footer">
                <span>Энилекс.Контроль</span>
                <Link to="/offer">Оферта</Link>
                <Link to="/privacy">Конфиденциальность</Link>
                <Link to="/blog">Блог</Link>
            </footer>
        </div>
    );
}
